import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Checkbox,
  VStack,
  UnorderedList,
  ListItem,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';

import { CONSENT_METHOD_VERBAL, CONSENT_METHOD_WRITTEN } from '../../constants';

interface Driver {
  id: number;
  name: string;
}

interface OrderReportsConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (method: number) => void;
  isLoading?: boolean;
  carrierName: string;
  driversWithLicense: Driver[];
  driversWithoutLicense: Driver[];
}

const OrderReportsConsentModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  carrierName,
  driversWithLicense,
  driversWithoutLicense,
}: OrderReportsConsentModalProps) => {
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [method, setMethod] = useState<string>(String(CONSENT_METHOD_VERBAL));

  const hasDriversWithLicense = driversWithLicense.length > 0;
  const hasDriversWithoutLicense = driversWithoutLicense.length > 0;

  const handleClose = () => {
    setIsConsentChecked(false);
    setMethod(String(CONSENT_METHOD_VERBAL));
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(parseInt(method, 10));
  };

  const renderDriversList = () => {
    if (!hasDriversWithLicense && hasDriversWithoutLicense) {
      return (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            No drivers have license numbers. MVR and DASH reports will not be
            ordered.
          </Text>
        </Alert>
      );
    }

    return (
      <>
        {hasDriversWithLicense && (
          <>
            <Text>
              {hasDriversWithoutLicense
                ? 'MVR and Market Specific DASH Reports will be ordered for:'
                : `You have chosen to proceed with '${carrierName}' market. MVR and Market Specific DASH Reports will be ordered for the following:`}
            </Text>
            <UnorderedList spacing={1} pl={4} color="red.600">
              {driversWithLicense.map((driver) => (
                <ListItem key={driver.id} fontWeight="500">
                  {driver.name}
                </ListItem>
              ))}
            </UnorderedList>
          </>
        )}

        {hasDriversWithoutLicense && hasDriversWithLicense && (
          <Box
            mt={2}
            p={3}
            bg="orange.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor="orange.200"
          >
            <Text fontSize="sm" fontWeight="600" color="orange.700" mb={1}>
              Skipped (no license number):
            </Text>
            <UnorderedList spacing={1} pl={4} fontSize="sm" color="orange.700">
              {driversWithoutLicense.map((driver) => (
                <ListItem key={driver.id}>{driver.name}</ListItem>
              ))}
            </UnorderedList>
          </Box>
        )}
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="lg" p={2}>
        <ModalCloseButton />

        <ModalBody pt={8} pb={4}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="xl" fontWeight="bold">
              {hasDriversWithLicense
                ? 'Ordered Reports'
                : 'Proceed Without Reports'}
            </Text>

            {renderDriversList()}
          </VStack>
        </ModalBody>

        {hasDriversWithLicense && (
          <Box mx={6} p={4} borderRadius="md" borderWidth="1px">
            <Text fontWeight="600" mb={3}>
              Please confirm the following:
            </Text>
            <Checkbox
              isChecked={isConsentChecked}
              onChange={(e) => setIsConsentChecked(e.target.checked)}
              colorScheme="blue"
              alignItems="flex-start"
            >
              <Text fontSize="sm" fontStyle="italic" color="gray.600" mt={-0.5}>
                I confirm that I have received authorization from the client(s)
                to obtain, verify, and exchange personal information for the
                purposes of underwriting, rating, and issuing insurance,
                including policy and driver history reports from the appropriate
                authorities and databases.
              </Text>
            </Checkbox>

            <FormControl mt={4}>
              <FormLabel fontSize="sm" fontWeight="600">
                Consent Method
              </FormLabel>
              <RadioGroup value={method} onChange={setMethod}>
                <VStack align="start" spacing={2}>
                  <Radio
                    value={String(CONSENT_METHOD_VERBAL)}
                    colorScheme="blue"
                  >
                    Verbal Consent
                  </Radio>
                  <Radio
                    value={String(CONSENT_METHOD_WRITTEN)}
                    colorScheme="blue"
                  >
                    Written Consent
                  </Radio>
                </VStack>
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        <ModalFooter gap={3} pt={6}>
          <Button
            variant="brand"
            onClick={handleConfirm}
            isLoading={isLoading}
            isDisabled={hasDriversWithLicense && !isConsentChecked}
            minW="100px"
          >
            Confirm
          </Button>
          <Button
            variant="brand"
            onClick={handleClose}
            isDisabled={isLoading}
            minW="100px"
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OrderReportsConsentModal;
